import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const COLORS = ['#0d9488', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#ec4899'];

const PopularDestinations = () => {
  const [loading, setLoading] = useState(true);
  const [destinations, setDestinations] = useState([]);
  const [topDestinations, setTopDestinations] = useState([]);
  const [destinationsByType, setDestinationsByType] = useState([]);

  useEffect(() => {
    fetchDestinationData();
  }, []);

  const fetchDestinationData = async () => {
    try {
      setLoading(true);

      // Fetch all destinations
      const { data: destData, error: destError } = await supabase
        ?.from('destinations')
        ?.select('*')
        ?.eq('is_active', true)
        ?.order('popularity_score', { ascending: false });

      if (destError) throw destError;

      setDestinations(destData || []);

      // Get top 6 destinations by popularity
      const top = (destData || [])?.slice(0, 6)?.map(d => ({
        name: d?.name,
        popularity: d?.popularity_score || 0,
        bookings: d?.total_bookings || 0
      }));
      setTopDestinations(top);

      // Group by destination type
      const typeGroups = {};
      destData?.forEach(d => {
        const type = d?.destination_type || 'other';
        if (!typeGroups?.[type]) {
          typeGroups[type] = { type, count: 0, totalPopularity: 0 };
        }
        typeGroups[type].count += 1;
        typeGroups[type].totalPopularity += d?.popularity_score || 0;
      });

      const typeData = Object?.values(typeGroups)?.map(g => ({
        type: g?.type?.charAt(0)?.toUpperCase() + g?.type?.slice(1),
        count: g?.count,
        avgPopularity: g?.count > 0 ? (g?.totalPopularity / g?.count)?.toFixed(1) : 0
      }));
      setDestinationsByType(typeData);

    } catch (error) {
      console.error('Error fetching destination data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-lg p-6 border border-teal-200">
          <div className="flex items-center justify-between mb-2">
            <Icon name="MapPin" size={24} className="text-teal-600" />
            <span className="text-teal-700 text-sm font-medium">Total Destinations</span>
          </div>
          <div className="text-3xl font-bold text-teal-900">
            {destinations?.length}
          </div>
          <div className="text-sm text-teal-600 mt-1">Active destinations</div>
        </div>

        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-6 border border-blue-200">
          <div className="flex items-center justify-between mb-2">
            <Icon name="Star" size={24} className="text-blue-600" />
            <span className="text-blue-700 text-sm font-medium">Avg Popularity</span>
          </div>
          <div className="text-3xl font-bold text-blue-900">
            {destinations?.length > 0
              ? (destinations?.reduce((sum, d) => sum + (d?.popularity_score || 0), 0) / destinations?.length)?.toFixed(1)
              : '0.0'}
          </div>
          <div className="text-sm text-blue-600 mt-1">Out of 10</div>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-6 border border-purple-200">
          <div className="flex items-center justify-between mb-2">
            <Icon name="TrendingUp" size={24} className="text-purple-600" />
            <span className="text-purple-700 text-sm font-medium">Featured</span>
          </div>
          <div className="text-3xl font-bold text-purple-900">
            {destinations?.filter(d => d?.is_featured)?.length}
          </div>
          <div className="text-sm text-purple-600 mt-1">Featured destinations</div>
        </div>
      </div>
      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Destinations Bar Chart */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Icon name="Award" size={20} className="mr-2 text-teal-600" />
            Top Destinations by Popularity
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={topDestinations} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="name" type="category" width={100} />
              <Tooltip />
              <Legend />
              <Bar dataKey="popularity" fill="#0d9488" name="Popularity Score" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Destination Types Pie Chart */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Icon name="PieChart" size={20} className="mr-2 text-blue-600" />
            Destinations by Type
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={destinationsByType}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ type, count }) => `${type}: ${count}`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="count"
              >
                {destinationsByType?.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS?.[index % COLORS?.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
      {/* Destination List */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Icon name="List" size={20} className="mr-2 text-gray-600" />
          All Destinations Overview
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Destination</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Type</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Country</th>
                <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Popularity</th>
                <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Featured</th>
              </tr>
            </thead>
            <tbody>
              {destinations?.slice(0, 10)?.map((dest, index) => (
                <tr key={dest?.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-4 text-sm text-gray-900">{dest?.name}</td>
                  <td className="py-3 px-4 text-sm text-gray-600">
                    <span className="px-2 py-1 bg-gray-100 rounded text-xs">
                      {dest?.destination_type}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-600">{dest?.country}</td>
                  <td className="py-3 px-4 text-center">
                    <div className="flex items-center justify-center">
                      <Icon name="Star" size={14} className="text-yellow-500 mr-1" />
                      <span className="text-sm font-medium text-gray-900">
                        {dest?.popularity_score?.toFixed(1) || '0.0'}
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-center">
                    {dest?.is_featured ? (
                      <Icon name="Check" size={18} className="text-green-600 mx-auto" />
                    ) : (
                      <Icon name="X" size={18} className="text-gray-300 mx-auto" />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PopularDestinations;